import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { Link } from 'react-router-dom';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import MenuComponent from 'components/Menu';
import ContactCTA from 'components/ContactCTA';
import FooterMedia from 'components/FooterMedia';
import Logo from 'assets/images/profitroll-logo-white.svg?react';
import JobAltoLogo from 'assets/icons/jobatlo-logo.svg?react';
import getLocale, { isLocalizedDomain, getLocalizedPath } from 'helpers/getLocale';
import { localeToFlagAlpha2 } from 'helpers/flags';
// Moved out of src/index.jsx: this 86 KB stylesheet was previously in the
// entry chunk and blocking render even though only the Footer + Localizations
// page render flag icons. Now it ships with the Footer chunk.
import 'flag-icons/css/flag-icons.min.css';

const Background = styled('section')(({ theme, backgroundColor }) => ({
  backgroundColor: backgroundColor || theme.palette.textPrimary.main,
  zIndex: 10,
  position: 'relative',
  width: '100%',
  overflowX: 'hidden'
}));

const FooterWrapperInner = styled('footer')(({ theme, backgroundColor }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  backgroundColor: backgroundColor || theme.palette.textPrimary.main,
  padding: '65px 100px',
  gap: 40,
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  ...theme.wrapper,
  [theme.breakpoints.down('md')]: {
    padding: 30,
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    '& nav': {
      marginTop: 20
    }
  }
}));

const FooterText = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 400,
  lineHeight: '16px',
  marginBottom: 18,
  maxWidth: 500,
  color: theme.palette.white.main,
  [theme.breakpoints.down('sm')]: {
    textAlign: 'center'
  }
}));

const FlexLogoInner = styled('div')(({ theme }) => ({
  width: '50%',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%'
  }
}));

const MenuWrapper = styled('div')(({ theme }) => ({
  width: '50%',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    marginTop: 20
  }
}));

const LogoWrapperInner = styled(isLocalizedDomain() ? JobAltoLogo : Logo)(({ theme }) => ({
  width: 150,
  height: 44,
  marginBottom: 20,
  marginTop: 18,
  [theme.breakpoints.down('md')]: {
    position: 'static',
    width: 87,
    height: 35,
    marginBottom: 15
  }
}));

const ChosenCountryWrapper = styled(Typography)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 12,
  color: '#EDF1F3',
  lineHeight: '16px'
}));

const MiniFlag = styled('span')(() => ({
  display: 'inline-block',
  width: 16,
  height: 12,
  borderRadius: 2
}));

const LinkStyled = styled(Link)(() => ({
  color: '#B9F833',
  textDecoration: 'underline',
  fontSize: 12,
  fontFamily: 'Rubik-Regular'
}));

const Footer = ({ backgroundColor }) => {
  const t = useTranslate('Footer');
  const locale = getLocale();
  const flagCode = localeToFlagAlpha2(locale);

  return (
    <Background backgroundColor={backgroundColor}>
      <FooterWrapperInner backgroundColor={backgroundColor}>
        <FlexLogoInner>
          <div>
            <ChosenCountryWrapper>
              {t('YourCountry')}: <MiniFlag className={`fi fi-${flagCode}`} />{' '}
              {t(`country_${locale}`)} ({String(t(locale)).toLocaleLowerCase()})
            </ChosenCountryWrapper>
            <LinkStyled to={getLocalizedPath('/language-selection')}>{t('Change')}</LinkStyled>
          </div>
          <LogoWrapperInner />

          <FooterText>{t('AllRightsReserved')}</FooterText>
          <FooterText>{t('Policy')}</FooterText>
          <FooterText>
            <LinkStyled to={getLocalizedPath('/professions/all')}>{t('AllProfessions')}</LinkStyled>
          </FooterText>
          <FooterText>
            <ContactCTA />
          </FooterText>
        </FlexLogoInner>
        <MenuWrapper>
          <MenuComponent isFooter={true} />
          <FooterMedia />
        </MenuWrapper>
      </FooterWrapperInner>
    </Background>
  );
};

export default Footer;
